import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import productService from '../../services/productService';
import ProductTable from '../../components/products/ProductTable';
import BulkActionsMenu from '../../components/products/BulkActionsMenu';
import RecentProducts from '../../components/products/RecentProducts';
import ProductAnalytics from '../../components/analytics/ProductAnalytics';
import { exportProductsToCSV, downloadCSV, parseCSVToProducts, validateImportedProducts } from '../../utils/csvUtils';
import './ProductListPage.css';

function ProductListPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const [categories, setCategories] = useState([]);

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [stockFilter, setStockFilter] = useState(''); // 'in-stock', 'low-stock', 'out-of-stock'
  const [featuredFilter, setFeaturedFilter] = useState(''); // 'featured', 'not-featured'

  // Search presets
  const [searchPresets, setSearchPresets] = useState([]);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);

  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, status: 'idle' });
  const [importResults, setImportResults] = useState({ success: [], errors: [] });
  const fileInputRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page - 1, // Convert to 0-indexed for Spring Data
        limit: pagination.limit,
        search,
        status: statusFilter,
        category: categoryFilter,
        sort,
      };

      // Add advanced filters if set
      if (priceMin) params.priceMin = priceMin;
      if (priceMax) params.priceMax = priceMax;
      if (stockFilter) params.stockFilter = stockFilter;
      if (featuredFilter === 'featured') params.isFeatured = true;
      if (featuredFilter === 'not-featured') params.isFeatured = false;

      const response = await productService.getProducts(params);

      // Handle Spring Data pagination format
      const springData = response.data;
      const productsData = springData.content || [];

      // Map backend sortOrder to frontend displayOrder for each product
      const mappedProducts = productsData.map(product => {
        if (product.images && Array.isArray(product.images)) {
          product.images = product.images.map(img => ({
            ...img,
            displayOrder: img.sortOrder  // Map sortOrder → displayOrder
          }));
        }
        return product;
      });

      setProducts(mappedProducts);
      setPagination((prev) => ({
        ...prev,
        total: springData.totalElements,
        totalPages: springData.totalPages,
      }));
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error fetching products:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter, categoryFilter, sort, priceMin, priceMax, stockFilter, featuredFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await productService.getCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Load search presets from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('product-search-presets');
      if (stored) {
        setSearchPresets(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Failed to load search presets:', err);
    }
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (newSort) => {
    setSort(newSort);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setSelectedIds([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(products.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productService.deleteProduct(id);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
      console.error('Error deleting product:', err);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await productService.getProduct(id);
      const originalProduct = response.data;

      // Map backend sortOrder to frontend displayOrder
      if (originalProduct.images && Array.isArray(originalProduct.images)) {
        originalProduct.images = originalProduct.images.map(img => ({
          ...img,
          displayOrder: img.sortOrder  // Map sortOrder → displayOrder
        }));
      }

      // Generate new SKU by adding "-COPY" suffix or incrementing number
      let newSku = `${originalProduct.sku}-COPY`;

      // Check if SKU already ends with -COPY or -COPY-N pattern
      const copyMatch = originalProduct.sku.match(/-COPY(-\d+)?$/);
      if (copyMatch) {
        const copyNumber = copyMatch[1] ? parseInt(copyMatch[1].substring(1)) + 1 : 2;
        newSku = originalProduct.sku.replace(/-COPY(-\d+)?$/, `-COPY-${copyNumber}`);
      }

      // Create duplicate with modified fields
      const duplicateData = {
        ...originalProduct,
        id: undefined, // Remove ID to create new product
        sku: newSku,
        name: `${originalProduct.name} (Copy)`,
        status: 'DRAFT', // Set to draft
        isFeatured: false, // Don't duplicate featured status
        createdAt: undefined,
        updatedAt: undefined,
      };

      // Create the duplicate
      const createResponse = await productService.createProduct(duplicateData);
      const newProductId = createResponse.data.id;

      toast.success('Product duplicated successfully');

      // Redirect to edit the new product
      window.location.href = `/products/${newProductId}/edit`;
    } catch (err) {
      toast.error('Failed to duplicate product');
      console.error('Error duplicating product:', err);
    }
  };

  const handleBulkAction = async (action, categoryId = null) => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one product');
      return;
    }

    try {
      let message = '';

      switch (action) {
        case 'publish':
          await productService.bulkUpdateStatus(selectedIds, 'ACTIVE');
          message = 'Products published successfully';
          break;
        case 'draft':
          await productService.bulkUpdateStatus(selectedIds, 'DRAFT');
          message = 'Products set to draft successfully';
          break;
        case 'archive':
          await productService.bulkUpdateStatus(selectedIds, 'ARCHIVED');
          message = 'Products archived successfully';
          break;
        case 'feature':
          await productService.bulkUpdateField(selectedIds, 'isFeatured', true);
          message = 'Products marked as featured';
          break;
        case 'unfeature':
          await productService.bulkUpdateField(selectedIds, 'isFeatured', false);
          message = 'Products removed from featured';
          break;
        case 'assign-category':
          if (!categoryId) {
            toast.error('Please select a category');
            return;
          }
          await productService.bulkAssignCategory(selectedIds, categoryId);
          message = 'Category assigned successfully';
          break;
        case 'delete':
          await productService.bulkDelete(selectedIds);
          message = 'Products deleted successfully';
          break;
        default:
          toast.error('Unknown action');
          return;
      }

      toast.success(message);
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      toast.error(`Failed to perform bulk action: ${err.message || 'Unknown error'}`);
      console.error('Error performing bulk action:', err);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCategoryFilter('');
    setSort('createdAt:desc');
    setPriceMin('');
    setPriceMax('');
    setStockFilter('');
    setFeaturedFilter('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const saveCurrentPreset = () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    const preset = {
      id: Date.now(),
      name: presetName,
      filters: {
        search,
        statusFilter,
        categoryFilter,
        priceMin,
        priceMax,
        stockFilter,
        featuredFilter,
        sort,
      },
    };

    const newPresets = [...searchPresets, preset];
    setSearchPresets(newPresets);
    localStorage.setItem('product-search-presets', JSON.stringify(newPresets));
    setShowPresetModal(false);
    setPresetName('');
    toast.success('Search preset saved');
  };

  const loadPreset = (preset) => {
    const { filters } = preset;
    setSearch(filters.search || '');
    setStatusFilter(filters.statusFilter || '');
    setCategoryFilter(filters.categoryFilter || '');
    setPriceMin(filters.priceMin || '');
    setPriceMax(filters.priceMax || '');
    setStockFilter(filters.stockFilter || '');
    setFeaturedFilter(filters.featuredFilter || '');
    setSort(filters.sort || 'createdAt:desc');
    setPagination((prev) => ({ ...prev, page: 1 }));
    toast.success(`Loaded preset: ${preset.name}`);
  };

  const deletePreset = (presetId) => {
    const newPresets = searchPresets.filter((p) => p.id !== presetId);
    setSearchPresets(newPresets);
    localStorage.setItem('product-search-presets', JSON.stringify(newPresets));
    toast.success('Preset deleted');
  };

  const hasAdvancedFilters = priceMin || priceMax || stockFilter || featuredFilter;

  const handleExportCSV = async () => {
    try {
      // Fetch all products (without pagination) for export
      const response = await productService.getProducts({
        page: 0,
        size: 10000, // Large number to get all products
      });

      const allProducts = response.data.content || [];

      if (allProducts.length === 0) {
        toast.warning('No products to export');
        return;
      }

      const csv = exportProductsToCSV(allProducts);
      const timestamp = new Date().toISOString().split('T')[0];
      downloadCSV(csv, `products-${timestamp}.csv`);

      toast.success(`Exported ${allProducts.length} products successfully`);
    } catch (err) {
      toast.error('Failed to export products');
      console.error('Error exporting products:', err);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setImportFile(file);
      setShowImportModal(true);
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) return;

    setImportProgress({ current: 0, total: 0, status: 'reading' });

    try {
      // Read file
      const text = await importFile.text();
      const { products: parsedProducts, errors: parseErrors } = parseCSVToProducts(text);

      if (parseErrors.length > 0) {
        setImportResults({ success: [], errors: parseErrors });
        setImportProgress({ current: 0, total: 0, status: 'error' });
        return;
      }

      // Validate products
      const validationErrors = validateImportedProducts(parsedProducts);
      if (validationErrors.length > 0) {
        setImportResults({ success: [], errors: validationErrors });
        setImportProgress({ current: 0, total: 0, status: 'error' });
        return;
      }

      // Import products
      setImportProgress({ current: 0, total: parsedProducts.length, status: 'importing' });

      const results = { success: [], errors: [] };

      for (let i = 0; i < parsedProducts.length; i++) {
        const product = parsedProducts[i];

        try {
          if (product.id) {
            // Update existing product
            await productService.updateProduct(product.id, product);
            results.success.push({ row: i + 2, sku: product.sku, action: 'updated' });
          } else {
            // Create new product
            await productService.createProduct(product);
            results.success.push({ row: i + 2, sku: product.sku, action: 'created' });
          }

          setImportProgress({ current: i + 1, total: parsedProducts.length, status: 'importing' });
        } catch (err) {
          results.errors.push({
            row: i + 2,
            sku: product.sku,
            errors: [err.response?.data?.message || err.message || 'Failed to import'],
          });
        }
      }

      setImportResults(results);
      setImportProgress({ current: parsedProducts.length, total: parsedProducts.length, status: 'completed' });

      // Refresh product list
      fetchProducts();

      toast.success(`Imported ${results.success.length} products successfully`);
    } catch (err) {
      toast.error('Failed to import CSV file');
      console.error('Error importing CSV:', err);
      setImportProgress({ current: 0, total: 0, status: 'error' });
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportProgress({ current: 0, total: 0, status: 'idle' });
    setImportResults({ success: [], errors: [] });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasActiveFilters = search || statusFilter || categoryFilter || hasAdvancedFilters;

  return (
    <div className="product-list-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Products</h1>
          <p>Manage your product catalog</p>
        </div>
        <div className="page-header-actions">
          {/* TODO: Review and implement Export functionality
          <button className="btn btn-secondary" onClick={handleExportCSV} title="Export to CSV">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          */}
          {/* TODO: Review and implement Import functionality
          <button className="btn btn-secondary" onClick={handleImportClick} title="Import from CSV">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          */}
          {/* TODO: Review and implement Featured Products page
          <Link to="/products/featured" className="btn btn-secondary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
            </svg>
            Featured
          </Link>
          */}
          <Link to="/products/new" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Product
          </Link>
        </div>
      </div>

      {/* Analytics Dashboard */}
      <ProductAnalytics />

      <div className="filters-section">
        <div className="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className="filter-group">
          <select value={statusFilter} onChange={handleStatusChange}>
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>

          <select value={categoryFilter} onChange={handleCategoryChange}>
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <button
            className={`btn btn-secondary ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="Advanced filters"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
            </svg>
            {hasAdvancedFilters && <span className="filter-badge">{Object.values({ priceMin, priceMax, stockFilter, featuredFilter }).filter(Boolean).length}</span>}
          </button>

          {searchPresets.length > 0 && (
            <div className="preset-dropdown">
              <button className="btn btn-secondary dropdown-toggle">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
                Presets
              </button>
              <div className="dropdown-menu">
                {searchPresets.map((preset) => (
                  <div key={preset.id} className="dropdown-item">
                    <button onClick={() => loadPreset(preset)} className="preset-load-btn">
                      {preset.name}
                    </button>
                    <button onClick={() => deletePreset(preset.id)} className="preset-delete-btn" title="Delete preset">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowPresetModal(true)} title="Save current filters">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
              </button>
              <button className="btn btn-text" onClick={clearFilters}>
                Clear filters
              </button>
            </>
          )}
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="advanced-filters-panel">
            <h4>Advanced Filters</h4>
            <div className="advanced-filters-grid">
              <div className="filter-item">
                <label>Price Range</label>
                <div className="price-range">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="filter-item">
                <label>Stock Status</label>
                <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}>
                  <option value="">All Stock Levels</option>
                  <option value="in-stock">In Stock</option>
                  <option value="low-stock">Low Stock</option>
                  <option value="out-of-stock">Out of Stock</option>
                </select>
              </div>

              <div className="filter-item">
                <label>Featured Status</label>
                <select value={featuredFilter} onChange={(e) => setFeaturedFilter(e.target.value)}>
                  <option value="">All Products</option>
                  <option value="featured">Featured Only</option>
                  <option value="not-featured">Not Featured</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedIds.length > 0 && (
        <BulkActionsMenu
          selectedCount={selectedIds.length}
          categories={categories}
          onAction={handleBulkAction}
          onClearSelection={() => setSelectedIds([])}
        />
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchProducts}>Retry</button>
        </div>
      )}

      <ProductTable
        products={products}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onSelectAll={handleSelectAll}
        onSelectOne={handleSelectOne}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        sort={sort}
        onSortChange={handleSortChange}
      />

      {!isLoading && pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={pagination.page === 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {!isLoading && products.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
          </div>
          <h3>No products found</h3>
          <p>
            {hasActiveFilters
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by adding your first product.'}
          </p>
          {!hasActiveFilters && (
            <Link to="/products/new" className="btn btn-primary">
              Add Product
            </Link>
          )}
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Products from CSV</h3>
              <button className="modal-close" onClick={closeImportModal} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {importProgress.status === 'idle' && (
                <>
                  <p className="modal-text">
                    Selected file: <strong>{importFile?.name}</strong>
                  </p>
                  <p className="modal-text">
                    This will import products from the CSV file. Products with an ID will be updated, products without an ID will be created.
                  </p>
                  <div className="import-info">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                    </svg>
                    <span>Make sure your CSV file follows the export format.</span>
                  </div>
                </>
              )}

              {(importProgress.status === 'reading' || importProgress.status === 'importing') && (
                <div className="import-progress">
                  <div className="progress-info">
                    <span>
                      {importProgress.status === 'reading' ? 'Reading file...' : `Importing products: ${importProgress.current} / ${importProgress.total}`}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: importProgress.total > 0 ? `${(importProgress.current / importProgress.total) * 100}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              )}

              {importProgress.status === 'completed' && (
                <div className="import-results">
                  <div className="results-summary">
                    <div className="result-stat success">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{importResults.success.length} succeeded</span>
                    </div>
                    {importResults.errors.length > 0 && (
                      <div className="result-stat error">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{importResults.errors.length} failed</span>
                      </div>
                    )}
                  </div>

                  {importResults.errors.length > 0 && (
                    <div className="error-list">
                      <h4>Errors:</h4>
                      <div className="error-items">
                        {importResults.errors.slice(0, 10).map((error, index) => (
                          <div key={index} className="error-item">
                            <strong>Row {error.row} {error.sku ? `(${error.sku})` : ''}:</strong>
                            <ul>
                              {(Array.isArray(error.errors) ? error.errors : [error.error]).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        {importResults.errors.length > 10 && (
                          <p className="more-errors">...and {importResults.errors.length - 10} more errors</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {importProgress.status === 'error' && (
                <div className="import-error">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <h4>Import Failed</h4>
                  <p>The CSV file contains errors. Please fix them and try again.</p>

                  {importResults.errors.length > 0 && (
                    <div className="error-list">
                      <div className="error-items">
                        {importResults.errors.slice(0, 10).map((error, index) => (
                          <div key={index} className="error-item">
                            <strong>Row {error.row} {error.sku ? `(${error.sku})` : ''}:</strong>
                            <ul>
                              {(Array.isArray(error.errors) ? error.errors : [error.error]).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                        {importResults.errors.length > 10 && (
                          <p className="more-errors">...and {importResults.errors.length - 10} more errors</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              {importProgress.status === 'idle' && (
                <>
                  <button className="btn btn-secondary" onClick={closeImportModal}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleImportCSV}>
                    Start Import
                  </button>
                </>
              )}

              {(importProgress.status === 'completed' || importProgress.status === 'error') && (
                <button className="btn btn-primary" onClick={closeImportModal}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Preset Modal */}
      {showPresetModal && (
        <div className="modal-overlay" onClick={() => setShowPresetModal(false)}>
          <div className="modal-content preset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Search Preset</h3>
              <button className="modal-close" onClick={() => setShowPresetModal(false)} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-text">
                Save your current search filters and settings as a preset for quick access later.
              </p>
              <div className="form-group">
                <label>Preset Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Active Products Under $50"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveCurrentPreset();
                    }
                  }}
                  autoFocus
                />
              </div>

              <div className="preset-summary">
                <h4>Current Filters:</h4>
                <ul>
                  {search && <li>Search: "{search}"</li>}
                  {statusFilter && <li>Status: {statusFilter}</li>}
                  {categoryFilter && <li>Category: {categoryFilter}</li>}
                  {priceMin && <li>Min Price: ${priceMin}</li>}
                  {priceMax && <li>Max Price: ${priceMax}</li>}
                  {stockFilter && <li>Stock: {stockFilter}</li>}
                  {featuredFilter && <li>Featured: {featuredFilter}</li>}
                </ul>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowPresetModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveCurrentPreset}>
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Products Widget */}
      <RecentProducts />
    </div>
  );
}

export default ProductListPage;
